'use client'

import useSWR, { SWRConfiguration } from 'swr'
import { api } from '@/api'
import { ONE_HOUR_IN_SECONDS, ONE_SECOND_IN_MILLISECONDS } from '@/constants'
import { ErrorCode } from '@/errors/errorCode'
import { logError } from '@/errors/log-error'
import type { ShopMegaMenuData } from '@/types/api/shop-mega-menu'
import { useFeatureFlag } from './use-feature-flag'

// Same options the rest of the navigation data uses: the menu changes a few
// times a month, so an hourly refresh is plenty and refocusing a tab should not
// refetch it.
const fetchOptions: SWRConfiguration = {
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  refreshInterval: ONE_HOUR_IN_SECONDS * ONE_SECOND_IN_MILLISECONDS,
}

// Curated Shop mega menu content. Returns null while loading, when the
// `affiliateShop` flag is off, and when the fetch fails — callers fall back to
// a plain link to the shop, so a broken menu never blocks the navigation.
export const useShopMegaMenu = (): ShopMegaMenuData | null => {
  const { isEnabled } = useFeatureFlag()
  const shopEnabled = isEnabled('affiliateShop')

  const { data } = useSWR(
    shopEnabled ? 'shopMegaMenu' : null,
    async () => {
      const [error, megaMenu] = await api.feTofe.shop.getMegaMenu()
      if (error) {
        logError(ErrorCode.e00269, error)
        return null
      }
      return megaMenu
    },
    fetchOptions
  )

  return data ?? null
}
